// this represents one particular variation of a particular "extra item"
type AddOnVariations = {
  id: number;
  name: string;
  amount: number;
  isInCart: boolean;
};

// this represents all the possible "extra items" associated with a main food item
type AddOns = {
  id: number;
  name: string;
  isInCart: boolean;
  currentlySelectedVariationIndex: number;
  maximumSelectableVariationIndex: number;
  variations: AddOnVariations[];
};

// this represents the restaurant this order is from
type Restaurant = {
  id: number;
  name: string;
  fullAddress: string;
};

// this represents one main food item
type CartItem = {
  id: number;
  name: string;
  unitPrice: number;
  quantity: number;
  note: string;
  addOns: AddOns[];
};

// this represents the cart item price
type Price = {
  couponDiscount: number;
  foodItemsAmount: number;
  totalAmount: number;
};

// this represents an array of main food items
export type CartState = {
  cartItems: CartItem[];
  totalPrice: number;
  price: Price;
  note: string;
  isCartModified: boolean;
  associatedRestaurant: Restaurant | null;
};

export const inititalTotalCartState: CartState = {
  cartItems: [],
  totalPrice: 0,
  price: {
    couponDiscount: 0,
    foodItemsAmount: 0,
    totalAmount: 0,
  },
  note: '',
  isCartModified: false,
  associatedRestaurant: null,
};

type Action =
  | {
      type: 'INITIAL_CARTSTATE_CALCULATE';
      payload: {
        data: any;
      };
    }
  | {
      type: 'FOOD_ITEM_CHANGE';
      payload:
        | {
            for: 'main-food';
            mainItemIndex: number;
            type: 'increment' | 'decrement';
          }
        | {
            for: 'extra-food';
            mainItemIndex: number;
            extraItemIndex: number;
            type: 'increment' | 'decrement';
          };
    }
  | {
      type: 'EXTRA_ITEM_ADD_OR_REMOVE';
      payload: {
        mainItemIndex: number;
        extraItemIndex: number;
        isChecked: boolean;
      };
    }
  | {
      type: 'NOTE_CHANGE';
      payload:
        | {
            for: 'general-note';
            note: string;
          }
        | {
            for: 'menu-item-note';
            cartItemIndex: number;
            note: string;
          };
    };

export const cartStateReducer = (state: CartState, action: Action): CartState => {
  switch (action.type) {
    case 'INITIAL_CARTSTATE_CALCULATE':
      const cartItems = action.payload?.data?.cart_items;
      const restaurant = action.payload?.data?.restaurant;

      let initialCalculatedCartState: CartState = {
        cartItems: [],
        totalPrice: action.payload?.data?.price?.total_amount,
        price: {
          couponDiscount: action.payload?.data?.price?.coupon_discount,
          foodItemsAmount: action.payload?.data?.price?.food_items_amount,
          totalAmount: action.payload?.data?.price?.total_amount,
        },
        note: action.payload?.data?.note,
        isCartModified: false,
        associatedRestaurant: {
          id: restaurant?.restaurant_id,
          name: restaurant?.name,
          fullAddress: restaurant?.full_address,
        },
      };

      for (let i = 0; i < cartItems?.length; i++) {
        const associatedAddOns: AddOns[] = [];

        for (let j = 0; j < cartItems[i]?.menu_item?.item_choices?.length; j++) {
          const getPreviouslySelectedVariationIndex = cartItems[i]?.menu_item?.item_choices[j]?.addon_levels?.findIndex(
            (el: any) => el?.is_in_cart === true,
          );

          const obj: AddOns = {
            id: cartItems[i]?.menu_item?.item_choices[j]?.item_choice_id,
            name: cartItems[i]?.menu_item?.item_choices[j]?.name,
            isInCart: cartItems[i]?.menu_item?.item_choices[j]?.is_in_cart,
            currentlySelectedVariationIndex:
              getPreviouslySelectedVariationIndex === -1 ? 0 : getPreviouslySelectedVariationIndex,
            maximumSelectableVariationIndex: cartItems[i]?.menu_item?.item_choices[j]?.addon_levels?.length - 1,
            variations: cartItems[i]?.menu_item?.item_choices[j]?.addon_levels?.map((d: any) => ({
              id: d?.addon_level_id,
              name: d?.name,
              amount: d?.amount,
              isInCart: d?.is_in_cart,
            })),
          };

          associatedAddOns.push(obj);
        }

        const cartItem: CartItem = {
          id: cartItems[i]?.cart_item_id,
          name: cartItems[i]?.menu_item?.name,
          unitPrice: cartItems[i]?.menu_item?.price,
          quantity: cartItems[i]?.quantity,
          note: cartItems[i]?.note?.trim(),
          addOns: associatedAddOns,
        };

        initialCalculatedCartState.cartItems.push(cartItem);
      }

      return {
        ...initialCalculatedCartState,
      };

    case 'FOOD_ITEM_CHANGE': {
      if (action.payload.for === 'main-food') {
        const previousState = { ...state };

        previousState.isCartModified = true;

        const mainItemIndexToUpdate = action.payload.mainItemIndex;
        const previousMainItemQuantity = previousState.cartItems[mainItemIndexToUpdate].quantity;

        let newQuantityValue = 0;

        if (action.payload.type === 'decrement') {
          if (previousMainItemQuantity === 1) newQuantityValue = previousMainItemQuantity;
          else newQuantityValue = previousMainItemQuantity - 1;
        } else {
          if (previousMainItemQuantity === 10) {
            newQuantityValue = previousMainItemQuantity;
          } else newQuantityValue = previousMainItemQuantity + 1;
        }

        previousState.cartItems[mainItemIndexToUpdate].quantity = newQuantityValue;

        previousState.totalPrice = calculateTotalPrice(previousState);

        return previousState;
      } else {
        const previousState = { ...state };

        previousState.isCartModified = true;

        const mainItemIndexToUpdate = action.payload.mainItemIndex;
        const extraItemIndexToUpdate = action.payload.extraItemIndex;

        const previousCurrentlySelectedVariationIndex =
          previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].currentlySelectedVariationIndex;

        const maximumSelectableVariationIndex =
          previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].maximumSelectableVariationIndex;

        let newCurrentlySelectedVariationIndex = 0;

        if (action.payload.type === 'increment') {
          if (previousCurrentlySelectedVariationIndex === maximumSelectableVariationIndex) {
            newCurrentlySelectedVariationIndex = previousCurrentlySelectedVariationIndex;
          } else {
            newCurrentlySelectedVariationIndex = previousCurrentlySelectedVariationIndex + 1;
          }
        } else {
          if (previousCurrentlySelectedVariationIndex === 0) {
            newCurrentlySelectedVariationIndex = previousCurrentlySelectedVariationIndex;
          } else {
            newCurrentlySelectedVariationIndex = previousCurrentlySelectedVariationIndex - 1;
          }
        }

        previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].currentlySelectedVariationIndex =
          newCurrentlySelectedVariationIndex;

        previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].variations =
          previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].variations.map((d, index) => ({
            id: d.id,
            amount: d.amount,
            name: d.name,
            isInCart: newCurrentlySelectedVariationIndex === index ? true : false,
          }));

        previousState.totalPrice = calculateTotalPrice(previousState);

        return previousState;
      }
    }

    case 'EXTRA_ITEM_ADD_OR_REMOVE': {
      const previousState = { ...state };
      previousState.isCartModified = true;

      const mainItemIndexToUpdate = action.payload.mainItemIndex;
      const extraItemIndexToUpdate = action.payload.extraItemIndex;

      previousState.cartItems[mainItemIndexToUpdate].addOns[extraItemIndexToUpdate].isInCart = action.payload.isChecked;
      previousState.totalPrice = calculateTotalPrice(previousState);

      return previousState;
    }

    case 'NOTE_CHANGE': {
      const previousState = { ...state };
      previousState.isCartModified = true;

      if (action.payload.for === 'general-note') {
        previousState.note = action.payload.note;
        return previousState;
      } else {
        previousState.cartItems[action.payload.cartItemIndex].note = action.payload.note;
        return previousState;
      }
    }
  }
};

const calculateTotalPrice = (cartState: CartState): number => {
  const cartItems = cartState.cartItems;
  let totalPrice = 0;

  for (let i = 0; i < cartItems.length; i++) {
    totalPrice += cartItems[i].unitPrice * cartItems[i].quantity;

    for (let j = 0; j < cartItems[i].addOns.length; j++) {
      if (cartItems[i].addOns[j].isInCart === false) continue;
      else {
        for (let k = 0; k < cartItems[i].addOns[j].variations.length; k++) {
          if (cartItems[i].addOns[j].variations[k].isInCart) {
            totalPrice += cartItems[i].addOns[j].variations[k].amount;
          }
        }
      }
    }
  }
  if (cartState.price.couponDiscount) {
    return totalPrice - cartState.price.couponDiscount;
  } else {
    return totalPrice;
  }
};
